import { Fragment, useCallback, useState } from 'react'
import styled from 'styled-components/macro'

import { Layout, Button, useDialog } from '@loadsmart/loadsmart-ui'
import { Icon, Tooltip } from '@loadsmart/miranda-react'

import { screen } from 'legacy/styles/screen'

import { useConfigContext } from 'config/ui/ConfigContext'
import { StatusContent } from 'shipments/ui/StatusContent'
import { StatusTitle } from 'shipments/ui/StatusTitle'

import { type Stop } from '../domain/Shipment'
import { getStopEventAudit } from '../presenters/stopEventAuditPresenter'
import {
  getStopCheckClassName,
  getCheckedAt,
  getStopCheckTitle,
  getStopCheckEvent,
  getStopCheckInfo,
  getStopCheckIsCurrent,
  getStopCheckButtonLabel,
} from '../presenters/stopPresenter'
import StopCheckDialog from './ShipmentStopCheckDialog'

const StyledButton = styled(Button)`
  margin-left: 4em;
  margin-right: 1em;

  ${screen.md} {
    margin-left: 0;
  }
`

type ShipmentStopCheckedInOutProps = {
  type: 'in' | 'out'
  stop: Stop
  stopState: {
    index: number
    checkIn?: string
    checkOut?: string
  }
  lookupShipment: (uuid: string) => Promise<void>
  shipmentUUID: string
}

export function ShipmentStopCheckedInOut({
  type,
  stop,
  stopState,
  lookupShipment,
  shipmentUUID,
}: ShipmentStopCheckedInOutProps) {
  const [isLoading, setIsLoading] = useState(false)
  const enableCheckInOutEdit = useConfigContext().enableCheckInOutEdit.toBoolean()

  const { open, show, hide } = useDialog({
    open: false,
  })

  const title = getStopCheckTitle(type)
  const stopCheckIsCurrent = getStopCheckIsCurrent(type, stopState)
  const stopCheckClassName = getStopCheckClassName(type)
  const stopCheckInfo = getStopCheckInfo(stop, type)
  const stopCheckEvent = getStopCheckEvent(stop, type)
  const facilityTimezone = stop.facility?.timezone
  const audit = enableCheckInOutEdit
    ? getStopEventAudit(type, stopCheckEvent, facilityTimezone)
    : null
  const showTooltip = Boolean(stopCheckInfo && (facilityTimezone || audit))

  const onUpdateShipmentStatus = useCallback(() => {
    lookupShipment(shipmentUUID).then(() => {
      setIsLoading(false)
    })
  }, [lookupShipment, shipmentUUID])

  return (
    <Fragment>
      <Layout.Group
        className={`ls-PageShipmentsStatus-checkIn ${stopCheckIsCurrent}`}
        justify="space-between"
        align="center"
      >
        <Layout.Group>
          <StatusTitle>{title}</StatusTitle>
          <StatusContent className={stopCheckClassName}>
            <Layout.Group space="s" align="center">
              {getCheckedAt(stopCheckInfo, facilityTimezone)}
              {showTooltip && (
                <Tooltip
                  placement="right"
                  trigger="hover"
                  message={
                    <Layout.Stack space="s">
                      {facilityTimezone ? (
                        <Layout.Stack space="none">
                          <span>{facilityTimezone}</span>
                          <span>Facility location timezone</span>
                        </Layout.Stack>
                      ) : null}
                      {audit?.completedBy ? (
                        <Layout.Stack space="none">
                          <strong>{audit.completedBy}</strong>
                          {audit.completedAt ? <span>{audit.completedAt}</span> : null}
                        </Layout.Stack>
                      ) : null}
                      {audit?.reasonLabel ? (
                        <Layout.Stack space="none">
                          <span>{audit.reasonLabel}</span>
                          {audit.reasonNote ? <span>{audit.reasonNote}</span> : null}
                        </Layout.Stack>
                      ) : null}
                    </Layout.Stack>
                  }
                >
                  <Icon
                    name="question"
                    size="16px"
                    color="color-text-tertiary"
                    aria-label={`${title} details`}
                  />
                </Tooltip>
              )}
            </Layout.Group>
          </StatusContent>
        </Layout.Group>
        {!stopCheckInfo ? (
          <StyledButton onClick={show} scale="small" loading={isLoading}>
            {getStopCheckButtonLabel(type)}
          </StyledButton>
        ) : null}
        {stopCheckInfo ? (
          <StyledButton variant="tertiary" onClick={show} scale="small" loading={isLoading}>
            Edit
          </StyledButton>
        ) : null}
      </Layout.Group>
      <StopCheckDialog
        shipmentUUID={shipmentUUID}
        stopId={stop.id}
        previousDateTime={stopCheckInfo || ''}
        isOpen={open}
        hideDialog={hide}
        stopType={type}
        callback={onUpdateShipmentStatus}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        appointmentWindowEnd={stop.appointment?.scheduledWindowEnd}
        timezone={stop.facility?.timezone ?? stop.timezone}
        eventId={stopCheckEvent?.id}
        initialReasonCode={stopCheckEvent?.reasonCode}
        initialReasonNote={stopCheckEvent?.reasonNote}
      />
    </Fragment>
  )
}
